import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Building2, Search, Download, RefreshCw, AlertTriangle, X, CheckCircle, Eye, Edit, Trash2, BarChart3 } from 'lucide-react';
import {
  Box,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import MainService from '../../services/MainService';
import { useNavigate } from 'react-router-dom';
import DataColumnMenu from '../ui/DataColumnMenu';
import { columnHasActiveFilter, groupRows } from '../ui/columnMenuUtils';
import { useGroupCollapse, GroupByBanner, GroupHeaderLabel } from '../ui/groupCollapse';
import { flattenFiltersForApi } from '../../utils/listFilters';
import { PAGE_SIZE_OPTIONS, buildPageItems } from '../../utils/listPagination';
import { exportGroupedExcel } from '../../utils/excelExport';
import { formatDateCell } from '../ui/tableColumnConfigs';
import { GridText } from '../ui/gridCell';

const CLIENT_CELL = 'px-3 py-2 text-sm text-gray-900';

const CLIENT_COLUMNS = [
  { field: 'name', title: 'Name', widthClass: 'w-[12%]', hideable: true },
  { field: 'mobileno1', title: 'Mobile No1', widthClass: 'w-[10%]', hideable: true },
  { field: 'mobileno2', title: 'Mobile No2', widthClass: 'w-[10%]', hideable: true },
  { field: 'telno', title: 'Tel No', widthClass: 'w-[9%]', hideable: true },
  { field: 'emailid', title: 'Email', widthClass: 'w-[12%]', hideable: true },
  { field: 'address', title: 'Address', widthClass: 'w-[12%]', hideable: true },
  { field: 'emiratename', title: 'Emirate', widthClass: 'w-[9%]', hideable: true },
  { field: 'areaname', title: 'Area', widthClass: 'w-[9%]', hideable: true },
  { field: 'locationname', title: 'Location', widthClass: 'w-[9%]', hideable: true },
  { field: 'date', title: 'Created Date', widthClass: 'w-[9%]', hideable: true },
  { field: 'createdby', title: 'Created By', widthClass: 'w-[9%]', hideable: true },
  { field: 'actions', title: 'Options', widthClass: 'w-[8%]', hideable: true, sortable: false, filterable: false, groupable: false, align: 'center' },
];

const defaultVisibility = () =>
  CLIENT_COLUMNS.reduce((acc, col) => {
    acc[col.field] = true;
    return acc;
  }, {});

const getClientCellValue = (row, field) => {
  switch (field) {
    case 'name': return row.Name;
    case 'mobileno1': return row.MobileNo1;
    case 'mobileno2': return row.MobileNo2;
    case 'telno': return row.TelNo;
    case 'emailid': return row.EmailId;
    case 'address': return row.Address;
    case 'emiratename': return row.EmirateName;
    case 'areaname': return row.AreaName;
    case 'locationname': return row.LocationName;
    case 'date': return formatDateCell(row.Date);
    case 'createdby': return row.CreatedBy?.Name || '';
    default: return row[field];
  }
};

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchKeyword, setActiveSearchKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filtersByField, setFiltersByField] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(defaultVisibility);
  const [groupField, setGroupField] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const loadSeqRef = useRef(0);
  const pageSizeBeforeGroupRef = useRef(null);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchClients = useCallback(async ({
    page: pageArg,
    pageSize: pageSizeArg,
    search: searchArg,
    sortField: sortFieldArg,
    sortDirection: sortDirectionArg,
    filters: filtersArg,
  } = {}) => {
    const pageNum = pageArg ?? page;
    const size = pageSizeArg !== undefined ? pageSizeArg : pageSize;
    const search = searchArg !== undefined ? searchArg : activeSearchKeyword;
    const sField = sortFieldArg !== undefined ? sortFieldArg : sortField;
    const sDir = sortDirectionArg !== undefined ? sortDirectionArg : sortDirection;
    const filters = filtersArg !== undefined ? filtersArg : filtersByField;
    const seq = ++loadSeqRef.current;
    const isAll = size === 'all';

    try {
      setLoading(true);
      setError('');
      const body = {
        Skip: isAll ? 0 : (pageNum - 1) * Number(size),
        Take: isAll ? 0 : Number(size),
        Keyword: (search || '').trim() || null,
        SortField: sField || null,
        SortDir: sDir || 'asc',
        Filters: flattenFiltersForApi(filters),
      };

      const response = await MainService.ClientsList(body);
      if (seq !== loadSeqRef.current) return;
      setClients(Array.isArray(response?.Data) ? response.Data : []);
      setTotalCount(Number(response?.Total) || 0);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error('Error loading clients:', err);
      setError('Failed to load clients');
      setClients([]);
      setTotalCount(0);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [page, pageSize, activeSearchKeyword, sortField, sortDirection, filtersByField]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const effectivePageSize = pageSize === 'all' ? Math.max(totalCount, 1) : Number(pageSize);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalCount / effectivePageSize) || 1);
  const pageItems = useMemo(() => buildPageItems(page, totalPages), [page, totalPages]);
  const rangeFrom = totalCount === 0 ? 0 : (pageSize === 'all' ? 1 : (page - 1) * Number(pageSize) + 1);
  const rangeTo = totalCount === 0 ? 0 : (pageSize === 'all' ? totalCount : Math.min(page * Number(pageSize), totalCount));
  const colSpan = useMemo(
    () => CLIENT_COLUMNS.filter((c) => columnVisibility[c.field] !== false).length + 1,
    [columnVisibility]
  );
  const activeFilterCount = useMemo(
    () => Object.keys(filtersByField).filter((f) => columnHasActiveFilter(filtersByField, f)).length,
    [filtersByField]
  );

  const displayGroups = useMemo(() => {
    if (!groupField) return [{ key: null, label: null, rows: clients }];
    const mapped = clients.map((r) => ({ ...r, [groupField]: getClientCellValue(r, groupField) }));
    return groupRows(mapped, groupField);
  }, [clients, groupField]);

  const { isCollapsed, toggleGroup, collapseAll, expandAll } = useGroupCollapse(groupField);

  const groupKeys = useMemo(
    () => displayGroups.filter((g) => g.label != null).map((g) => g.key),
    [displayGroups]
  );

  const handleGroupBy = (field) => {
    if (!groupField) {
      pageSizeBeforeGroupRef.current = { pageSize, page };
    }
    setGroupField(field);
    if (pageSize !== 'all') {
      setPage(1);
      setPageSize('all');
    }
  };

  const handleUngroup = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    if (prev) {
      setPageSize(prev.pageSize);
      setPage(prev.page);
    }
  };

  const handleSearch = () => {
    const keyword = searchTerm.trim();
    setPage(1);
    setActiveSearchKeyword(keyword);
    fetchClients({ page: 1, search: keyword });
  };

  const handleRefresh = () => {
    setPage(1);
    setSearchTerm('');
    setActiveSearchKeyword('');
    setSortField('');
    setSortDirection('asc');
    setFiltersByField({});
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    fetchClients({
      page: 1,
      search: '',
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const handlePageSizeChange = (value) => {
    const next = value === 'all' ? 'all' : Number(value);
    setPageSize(next);
    setPage(1);
    fetchClients({ page: 1, pageSize: next });
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
    fetchClients({ page: nextPage });
  };

  const handleColumnSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
    fetchClients({ page: 1, sortField: field, sortDirection: direction });
  };

  const handleToggleColumn = (field) => {
    setColumnVisibility((prev) => {
      const nextVisible = { ...prev, [field]: prev[field] === false };
      const stillVisible = CLIENT_COLUMNS.some((c) => nextVisible[c.field] !== false);
      if (!stillVisible) return prev;
      return nextVisible;
    });
  };

  const handleApplyColumnFilter = (field, filter) => {
    const nextFilters = { ...filtersByField, [field]: filter };
    setFiltersByField(nextFilters);
    setPage(1);
    fetchClients({ page: 1, filters: nextFilters });
  };

  const handleClearColumnFilter = (field) => {
    const nextFilters = { ...filtersByField };
    delete nextFilters[field];
    setFiltersByField(nextFilters);
    setPage(1);
    fetchClients({ page: 1, filters: nextFilters });
  };

  const exportToExcel = () => {
    try {
      exportGroupedExcel({
        sheetName: 'Clients',
        fileName: `clients_${new Date().toISOString().split('T')[0]}.xlsx`,
        headers: ['#', 'Name', 'Mobile No1', 'Mobile No2', 'Tel No', 'Email', 'Address', 'Emirate', 'Area', 'Location', 'Created Date', 'Created By'],
        groups: displayGroups,
        groupFieldTitle: CLIENT_COLUMNS.find((c) => c.field === groupField)?.title || 'Group',
        mapRow: (client, serial) => [
          serial,
          client.Name,
          client.MobileNo1,
          client.MobileNo2,
          client.TelNo,
          client.EmailId,
          client.Address,
          client.EmirateName,
          client.AreaName,
          client.LocationName,
          formatDateCell(client.Date),
          client.CreatedBy?.Name || '',
        ],
      });
      setSuccess('Excel file exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      if (err?.message === 'No rows to export') {
        setError('No clients to export');
      } else {
        setError('Failed to export Excel file');
      }
    }
  };

  const handleContextMenu = (event, client) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
        : null
    );
    setSelectedClient(client);
  };

  const handleMenuClose = () => {
    setContextMenu(null);
    setSelectedClient(null);
  };

  const handleView = (client) => {
    handleMenuClose();
    console.log('View client:', client);
  };

  const handleEdit = (client) => {
    handleMenuClose();
    if (client?.Id) navigate(`/home/accounts/addClient/${client.Id}`);
  };

  const handlePriceChart = (client) => {
    handleMenuClose();
    if (client?.Id) {
      navigate(`/home/accounts/clients/${client.Id}/price-chart`, {
        state: { clientName: client.Name, mobileNo: client.MobileNo1 },
      });
    }
  };

  const handleDelete = (client) => {
    handleMenuClose();
    if (!client?.Id) return;
    setRowToDelete(client);
    setDeleteDialogOpen(true);
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  };

  const confirmDelete = async () => {
    if (!rowToDelete?.Id) return;
    try {
      setLoading(true);
      setDeleteDialogOpen(false);
      setError('');
      setSuccess('');
      const response = await MainService.DeleteClient(rowToDelete.Id);
      if (response === 'Success' || response === true || response === 'true') {
        setSuccess('Client deleted successfully');
        await fetchClients();
      } else {
        setError(response || 'Failed to delete client');
      }
    } catch (err) {
      console.error('Error deleting client:', err);
      setError('Failed to delete client');
    } finally {
      setLoading(false);
      setRowToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} className="flex-shrink-0 text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 shadow-lg">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button type="button" onClick={() => setSuccess('')} className="flex-shrink-0 text-green-500 hover:text-green-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex flex-col rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="mb-2 flex items-center">
              <Building2 className="mr-2 h-6 w-6 text-primary" />
              <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
            </div>
            <Button
              variant="contained"
              startIcon={<Building2 className="h-4 w-4" />}
              onClick={() => navigate('/home/accounts/addClient')}
              sx={{ backgroundColor: '#46955A', '&:hover': { backgroundColor: '#3d7d4f' } }}
            >
              Add Client
            </Button>
          </div>

          <div className="flex flex-col items-end gap-4 lg:flex-row">
            <div className="flex-1">
              <TextField
                fullWidth
                placeholder="Search by name or mobile number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search className="h-5 w-5 text-gray-400" />
                    </InputAdornment>
                  ),
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none', borderBottom: '1px solid #E5E7EB' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderBottom: '2px solid #46955A' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderBottom: '2px solid #46955A' },
                  },
                }}
                variant="outlined"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<Search className="h-4 w-4" />}
                sx={{ backgroundColor: '#46955A', '&:hover': { backgroundColor: '#3d7d4f' } }}
              >
                Search
              </Button>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{ color: '#46955A', '&:hover': { backgroundColor: '#f0f9f4' }, '&:disabled': { color: '#9ca3af' } }}
                >
                  <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export to Excel">
                <IconButton
                  onClick={exportToExcel}
                  disabled={clients.length === 0}
                  sx={{ color: '#46955A', '&:hover': { backgroundColor: '#f0f9f4' }, '&:disabled': { color: '#9ca3af' } }}
                >
                  <Download className="h-5 w-5" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>

        <div className="relative overflow-auto" style={{ height: 500 }}>
          {loading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2">
                <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-gray-600">Loading...</span>
              </div>
            </div>
          )}

          {(activeFilterCount > 0 || sortField) && (
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-[#f0f9f4] px-4 py-2 text-sm text-gray-700">
              <span>
                {sortField
                  ? `Sorted by ${CLIENT_COLUMNS.find((c) => c.field === sortField)?.title || sortField} (${sortDirection})`
                  : 'Filters active'}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} column filter(s)` : ''}
              </span>
              <button
                type="button"
                onClick={() => {
                  const prev = pageSizeBeforeGroupRef.current;
                  setFiltersByField({});
                  setSortField('');
                  setSortDirection('asc');
                  setGroupField(null);
                  pageSizeBeforeGroupRef.current = null;
                  setPage(1);
                  if (prev && prev.pageSize !== pageSize) {
                    setPageSize(prev.pageSize);
                  }
                  fetchClients({
                    page: 1,
                    pageSize: prev?.pageSize ?? pageSize,
                    sortField: '',
                    sortDirection: 'asc',
                    filters: {},
                  });
                }}
                className="text-primary hover:underline"
              >
                Clear sort & filters
              </button>
            </div>
          )}

          {groupField && (
            <GroupByBanner
              groupTitle={CLIENT_COLUMNS.find((c) => c.field === groupField)?.title || groupField}
              groupKeys={groupKeys}
              onUngroup={handleUngroup}
              onCollapseAll={collapseAll}
              onExpandAll={expandAll}
            />
          )}

          <table className="data-grid-table w-full min-w-[1400px]">
            <thead className="data-grid-thead sticky top-0 z-10">
              <tr>
                <th className="data-grid-th data-grid-th-serial w-12 min-w-[3rem] px-3 py-2.5 text-left">#</th>
                {CLIENT_COLUMNS.map((column) =>
                  columnVisibility[column.field] === false ? null : (
                    <DataColumnMenu
                      key={column.field}
                      column={column}
                      columns={CLIENT_COLUMNS}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleColumnSort}
                      columnVisibility={columnVisibility}
                      onToggleColumn={handleToggleColumn}
                      filtersByField={filtersByField}
                      onApplyFilter={handleApplyColumnFilter}
                      onClearFilter={handleClearColumnFilter}
                      groupField={groupField}
                      onGroupBy={handleGroupBy}
                      onUngroup={handleUngroup}
                      align={column.align}
                    />
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {clients.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-6 text-center text-gray-500">
                    {activeSearchKeyword ? `No clients found for "${activeSearchKeyword}"` : 'No clients available'}
                  </td>
                </tr>
              ) : (
                (() => {
                  let serial = 0;
                  return displayGroups.flatMap((group) => {
                    const block = [];
                    if (group.label != null) {
                      block.push(
                        <tr key={`group-${group.key}`} className="bg-gray-100">
                          <td colSpan={colSpan} className="px-3 py-2">
                            <GroupHeaderLabel
                              title={CLIENT_COLUMNS.find((c) => c.field === groupField)?.title}
                              label={group.label}
                              count={group.rows.length}
                              collapsed={isCollapsed(group.key)}
                              onToggle={() => toggleGroup(group.key)}
                            />
                          </td>
                        </tr>
                      );
                    }
                    if (group.label != null && isCollapsed(group.key)) {
                      return block;
                    }
                    group.rows.forEach((client, index) => {
                      serial += 1;
                      block.push(
                        <tr
                          key={client.Id || `${group.key}-${index}`}
                          className="cursor-context-menu hover:bg-gray-50"
                          onContextMenu={(e) => handleContextMenu(e, client)}
                        >
                          <td className={`table-cell-serial ${CLIENT_CELL}`}>
                            {groupField || pageSize === 'all' ? serial : (page - 1) * Number(pageSize) + serial}
                          </td>
                          {columnVisibility.name !== false && (
                            <td className={CLIENT_CELL}><GridText value={client.Name} className="font-medium text-gray-900" /></td>
                          )}
                          {columnVisibility.mobileno1 !== false && (
                            <td className={CLIENT_CELL}><GridText value={client.MobileNo1} /></td>
                          )}
                          {columnVisibility.mobileno2 !== false && (
                            <td className={CLIENT_CELL}><GridText value={client.MobileNo2} /></td>
                          )}
                          {columnVisibility.telno !== false && (
                            <td className={CLIENT_CELL}><GridText value={client.TelNo} /></td>
                          )}
                          {columnVisibility.emailid !== false && (
                            <td className={CLIENT_CELL}><GridText value={client.EmailId} /></td>
                          )}
                          {columnVisibility.address !== false && (
                            <td className={CLIENT_CELL}><GridText value={client.Address} /></td>
                          )}
                          {columnVisibility.emiratename !== false && (
                            <td className={CLIENT_CELL}><GridText value={client.EmirateName} /></td>
                          )}
                          {columnVisibility.areaname !== false && (
                            <td className={CLIENT_CELL}><GridText value={client.AreaName} /></td>
                          )}
                          {columnVisibility.locationname !== false && (
                            <td className={CLIENT_CELL}><GridText value={client.LocationName} /></td>
                          )}
                          {columnVisibility.date !== false && (
                            <td className={CLIENT_CELL}><GridText value={formatDateCell(client.Date)} /></td>
                          )}
                          {columnVisibility.createdby !== false && (
                            <td className={CLIENT_CELL}><GridText value={client.CreatedBy?.Name} /></td>
                          )}
                          {columnVisibility.actions !== false && (
                            <td className="table-cell-actions px-3 py-2 text-center text-sm">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handlePriceChart(client)}
                                  className="rounded-lg p-2 text-emerald-600 transition-colors hover:bg-emerald-50"
                                  title="Price chart"
                                >
                                  <BarChart3 className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleEdit(client)}
                                  className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50"
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(client)}
                                  className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    });
                    return block;
                  });
                })()
              )}
            </tbody>
          </table>
        </div>

        <Menu
          open={contextMenu !== null}
          onClose={handleMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          PaperProps={{
            sx: {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              borderRadius: '0.5rem',
              minWidth: 150,
              mt: 0.5,
            },
          }}
        >
          <MenuItem onClick={() => handleView(selectedClient)} sx={{ fontSize: '0.875rem', py: 1.5, '&:hover': { backgroundColor: '#f0f9f4' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Eye className="h-4 w-4" style={{ color: '#46955A' }} />
              <span>View</span>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleEdit(selectedClient)} sx={{ fontSize: '0.875rem', py: 1.5, '&:hover': { backgroundColor: '#f0f9f4' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Edit className="h-4 w-4" style={{ color: '#46955A' }} />
              <span>Edit</span>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handlePriceChart(selectedClient)} sx={{ fontSize: '0.875rem', py: 1.5, '&:hover': { backgroundColor: '#f0f9f4' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <BarChart3 className="h-4 w-4" style={{ color: '#46955A' }} />
              <span>Price chart</span>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleDelete(selectedClient)} sx={{ fontSize: '0.875rem', py: 1.5, color: '#DC2626', '&:hover': { backgroundColor: '#FEE2E2' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Trash2 className="h-4 w-4" />
              <span>Delete</span>
            </Box>
          </MenuItem>
        </Menu>

        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-3">
              <span>
                {totalCount === 0 ? '0 clients' : `${rangeFrom}–${rangeTo} of ${totalCount}`}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} filter(s)` : ''}
                {groupField
                  ? ` · grouped by ${CLIENT_COLUMNS.find((c) => c.field === groupField)?.title || groupField}`
                  : ''}
              </span>
              <label className="flex items-center gap-2">
                <span className="text-gray-500">Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  disabled={loading || !!groupField}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[#46955A] focus:outline-none"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={String(opt)} value={opt}>
                      {opt === 'all' ? 'All' : opt}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {!groupField && pageSize !== 'all' && (
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => goToPage(page - 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Prev
                </button>
                {pageItems.map((item, idx) =>
                  item === '…' ? (
                    <span key={`e-${idx}`} className="px-1.5 text-gray-400">…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      disabled={loading}
                      onClick={() => goToPage(item)}
                      className={`min-w-[2rem] rounded-lg px-2 py-1.5 text-sm transition-colors ${
                        item === page
                          ? 'bg-[#46955A] font-medium text-white'
                          : 'border border-gray-200 bg-white text-gray-700 hover:bg-[#f0f9f4]'
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  onClick={() => goToPage(page + 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {deleteDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border border-red-100 bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-full bg-red-100 p-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-red-900">Confirm Delete</h3>
            </div>
            <p className="mb-6 text-sm text-gray-600">
              Are you sure you want to delete the client{' '}
              <strong>&quot;{rowToDelete?.Name}&quot;</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={cancelDelete}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={loading}
                className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-2 h-4 w-4" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
